import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { CallLogTable, FullScreenLoader } from '@Components/index';
import CommonConstants from '@Constants/common.constants';
import ReportFiltersModal from '@Containers/Report/ReportFiltersModal';
import {
  CallLogReportQuery,
  PrintAndExportCallLogPayload,
} from '@Models/report.model';
import printReportsService from '@Services/printReports.service';
import { useGetCallLogsQuery } from '@Services/report.service';
import { BaseAlertProps, ReportTypes } from '@Typings/global.types';
import { downloadCsv, downloadPDF } from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { getCallLogReportRTKQuery } from '@Utils/report/report.utils';

import CallLogProps from './CallLog.types';
import { GetRows } from './CallLog.utils';

const CallLog: FunctionComponent<CallLogProps> = (
  props: CallLogProps
): JSX.Element => {
  /** HOOKS */
  const location = useLocation();
  const { triggerAlert } = useAlert();
  const { t } = useTranslation();

  /** PROPS */
  const { backBtnProps, isAdmin, ...resProps } = props;

  /** STATES */
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tableCheckboxChecked, setTableCheckboxChecked] = useState<
    readonly string[]
  >([]);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [showLoader, setShowLoader] = useState(false);

  /** QUERIES */
  const callLogReportQuery: CallLogReportQuery = useMemo(
    () => getCallLogReportRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const { data, isLoading, isFetching, error } = useGetCallLogsQuery(
    callLogReportQuery,
    { skip: !query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) }
  );

  /** LOADING STATE */
  const loading = isLoading || isFetching || showLoader;

  /** ROWS AND TOTAL_ROWS_COUNT */
  const rows = GetRows(data?.table || [], isAdmin);
  const totalRowsCount =
    (data && parseInt(data?.table[0]?.fullCount, 10)) ||
    CommonConstants.INITIAL_ROWS_PER_PAGE;

  /**
   * Creates a payload for Print/Export service
   * @param ids sessionIds
   * @param printAll Whether or not it is a global action and not a bulk action
   * @returns payload for print/export
   */
  const createPayloadForPrintAndExport = (
    ids: string[] = [],
    printAll = false
  ) => {
    const exportPayload: PrintAndExportCallLogPayload = {
      ...callLogReportQuery,
    };
    if (ids && ids.length && !printAll) {
      exportPayload.sessionId = [...ids];
    }

    // Call Log Reports for particular session IDs
    if (
      callLogReportQuery.sessionId &&
      !Array.isArray(callLogReportQuery.sessionId)
    ) {
      exportPayload.sessionId = [callLogReportQuery.sessionId];
    }
    return exportPayload;
  };

  /**
   * Print Call Logs
   */
  const printCallLogHandler = (ids: string[] = [], bulkAction = false) => {
    setShowLoader(true);

    printReportsService
      .printCallLogReport(createPayloadForPrintAndExport(ids, bulkAction))
      .then((response) => {
        downloadPDF(response.data, t('reports.call_log_filename'));
        setTableCheckboxChecked([]);
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  /**
   * Print Call Logs
   */
  const exportCallLogHandler = (ids: string[] = [], bulkAction = false) => {
    setShowLoader(true);

    printReportsService
      .exportCallLogReport(createPayloadForPrintAndExport(ids, bulkAction))
      .then((response) => {
        if (response.status === 200) {
          if (response.data) {
            downloadCsv(response.data, t('reports.call_log_filename'));
          }
          setTableCheckboxChecked([]);
        }
      })
      .catch(() => {
        triggerAlert('GENERIC_ERROR_MESSAGE', setAlertState, 'error');
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** OPEN FILTERS MODAL */
  const openFilters = () => {
    setModalOpen(true);
  };

  return isLoading ? (
    <FullScreenLoader />
  ) : (
    <>
      <ReportFiltersModal
        isAdmin={isAdmin}
        initialReportType={ReportTypes.CallLog}
        open={modalOpen}
        setOpen={setModalOpen}
      />
      <CallLogTable
        exportAsPdfBtnProps={{
          onClick: () => printCallLogHandler(tableCheckboxChecked as string[]),
          disabled: loading,
        }}
        exportAsCsvBtnProps={{
          onClick: () => exportCallLogHandler(tableCheckboxChecked as string[]),
          disabled: loading,
        }}
        exportAllBtnProps={{
          disabled: loading,
        }}
        exportAllAsPdfOnClick={() => printCallLogHandler([], true)}
        exportAllAsCsvOnClick={() => exportCallLogHandler([], true)}
        alertState={alertState}
        backBtnProps={backBtnProps}
        showFiltersBtnProps={
          !backBtnProps
            ? {
                onClick: openFilters,
                disabled: loading,
              }
            : undefined
        }
        tableProps={{
          rows,
          checked: tableCheckboxChecked,
          setChecked: setTableCheckboxChecked,
          paginationProps: {
            count: totalRowsCount,
          },
          loading: isLoading || isFetching || showLoader,
        }}
        {...resProps}
      />
    </>
  );
};

export default CallLog;
